import React, { useEffect, useState, useRef } from 'react';
import { Button, Space, Card, message, Tag, Typography, Row, Col } from 'antd';
import {
  PlayCircleOutlined,
  StopOutlined,
  LinkOutlined,
  DisconnectOutlined,
  PauseCircleOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { Socket } from 'socket.io-client';
import { RESPONSE_TIMEOUT } from '@const/socket.const';
import type {
  BotStatusPayload,
  BrowserStatusPayload,
  WorkflowCompletedPayload,
  WorkflowErrorPayload,
  BotPausedPayload,
  BotResumedPayload,
  RemoteFramePayload,
} from '../../../../../types/socket.types';
import type {
  BotStatusPayload as BotStatusPayloadFromAPI,
  WorkflowStatus,
} from '../types/bot.types';
import { useCallback } from 'react';
const { Text } = Typography;

export interface BotControlComponentProps {
  botId: string;
  socket: Socket | null;
  initialStatusDetails?: BotStatusPayloadFromAPI;
  initialWorkflowStatus?: WorkflowStatus;
}

// Helper function to convert API BotStatusPayload to socket BotStatusPayload
const convertStatusDetails = (
  status: BotStatusPayloadFromAPI | undefined
): BotStatusPayload | null => {
  if (!status) return null;
  // Extract only the fields that match BotStatusPayload (exclude logs)
  const { logs, ...statusPayload } = status;
  return statusPayload as BotStatusPayload;
};

// Helper function to convert API WorkflowStatus to component workflow status
const convertWorkflowStatus = (
  status: WorkflowStatus | undefined
): {
  lastCompleted: WorkflowCompletedPayload | null;
  lastError: WorkflowErrorPayload | null;
} => {
  if (!status) {
    return {
      lastCompleted: null,
      lastError: null,
    };
  }

  // Check if it's a WorkflowErrorPayload (has 'error' property)
  if ('error' in status) {
    return {
      lastCompleted: null,
      lastError: status as WorkflowErrorPayload,
    };
  }

  // Otherwise it's a WorkflowCompletedPayload
  return {
    lastCompleted: status as WorkflowCompletedPayload,
    lastError: null,
  };
};

const BotControlComponent = ({
  botId,
  socket,
  initialStatusDetails,
  initialWorkflowStatus,
}: BotControlComponentProps) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState<{
    start: boolean;
    stop: boolean;
    connect: boolean;
    disconnect: boolean;
    pause: boolean;
    resume: boolean;
  }>({
    start: false,
    stop: false,
    connect: false,
    disconnect: false,
    pause: false,
    resume: false,
  });

  // Bot status state - initialize with initialStatusDetails if provided
  const [botStatus, setBotStatus] = useState<BotStatusPayload | null>(() =>
    convertStatusDetails(initialStatusDetails)
  );
  const [browserStatus, setBrowserStatus] = useState<BrowserStatusPayload | null>(null);
  const [workflowStatus, setWorkflowStatus] = useState<{
    lastCompleted: WorkflowCompletedPayload | null;
    lastError: WorkflowErrorPayload | null;
  }>(() => convertWorkflowStatus(initialWorkflowStatus));
  const [isPaused, setIsPaused] = useState<boolean>(() => {
    return initialStatusDetails?.isPaused ?? false;
  });

  // Remote streaming state
  const [isStreaming, setIsStreaming] = useState<boolean>(false);
  const [remoteFrame, setRemoteFrame] = useState<RemoteFramePayload | null>(null);
  const isStreamingRef = useRef<boolean>(false);

  // Frame throttling refs (must be at component level, not inside useEffect)
  const lastFrameUpdateRef = useRef<number>(0);
  const frameCountRef = useRef<number>(0);
  const previewContainerRef = useRef<HTMLDivElement>(null);

  const timeoutRefs = useRef<{
    start: NodeJS.Timeout | null;
    stop: NodeJS.Timeout | null;
    connect: NodeJS.Timeout | null;
    disconnect: NodeJS.Timeout | null;
    pause: NodeJS.Timeout | null;
    resume: NodeJS.Timeout | null;
  }>({
    start: null,
    stop: null,
    connect: null,
    disconnect: null,
    pause: null,
    resume: null,
  });

  const isConnected = socket?.connected ?? false;

  const clearActionTimeout = (
    action: 'start' | 'stop' | 'connect' | 'disconnect' | 'pause' | 'resume'
  ) => {
    if (timeoutRefs.current[action]) {
      clearTimeout(timeoutRefs.current[action]!);
      timeoutRefs.current[action] = null;
    }
  };

  const setLoadingWithTimeout = (
    action: 'start' | 'stop' | 'connect' | 'disconnect' | 'pause' | 'resume',
    value: boolean
  ) => {
    setLoading((prev) => ({ ...prev, [action]: value }));

    if (value) {
      // Set timeout to clear loading state if no response received
      clearActionTimeout(action);
      timeoutRefs.current[action] = setTimeout(() => {
        setLoading((prev) => ({ ...prev, [action]: false }));
        message.error(
          t('bots.control.timeout', { action: t(`bots.control.${action}`) }) ||
            `${t(`bots.control.${action}`) || action} request timed out`
        );
      }, RESPONSE_TIMEOUT);
    } else {
      clearActionTimeout(action);
    }
  };

  const checkConnection = (): boolean => {
    if (!socket || !isConnected) {
      message.warning(t('bots.control.notConnected') || 'Socket is not connected');
      return false;
    }
    return true;
  };

  // Handle initial status updates from props
  useEffect(() => {
    if (initialStatusDetails) {
      const convertedStatus = convertStatusDetails(initialStatusDetails);
      // Only update if we don't have existing status or if initial status is more recent
      setBotStatus((prevStatus) => {
        if (!prevStatus) {
          return convertedStatus;
        }
        // Compare timestamps - only update if initial status is more recent
        if (
          convertedStatus &&
          new Date(convertedStatus.timestamp).getTime() > new Date(prevStatus.timestamp).getTime()
        ) {
          return convertedStatus;
        }
        return prevStatus;
      });
      setIsPaused(initialStatusDetails.isPaused ?? false);
    }
  }, [initialStatusDetails]);

  useEffect(() => {
    if (initialWorkflowStatus) {
      const convertedWorkflow = convertWorkflowStatus(initialWorkflowStatus);
      // Only update if we don't have existing workflow status
      setWorkflowStatus((prevStatus) => {
        if (!prevStatus.lastCompleted && !prevStatus.lastError) {
          return convertedWorkflow;
        }
        // If we have existing status, keep it (socket updates take precedence)
        return prevStatus;
      });
    }
  }, [initialWorkflowStatus]);

  // Set up wheel event listener with passive: false to allow preventDefault
  useEffect(() => {
    const container = previewContainerRef.current;
    if (!container || !isStreaming) return;

    const handleWheel = (e: WheelEvent) => {
      if (!socket || !socket.connected || !remoteFrame) return;

      e.preventDefault();
      e.stopPropagation();

      socket.emit('remote-mouse', {
        type: 'wheel',
        x: 0.5, // Center
        y: 0.5,
        deltaX: e.deltaX,
        deltaY: e.deltaY,
      });
    };

    container.addEventListener('wheel', handleWheel, { passive: false });

    return () => {
      container.removeEventListener('wheel', handleWheel);
    };
  }, [socket, isStreaming, remoteFrame]);

  useEffect(() => {
    if (!socket) return;

    // Set up listeners for response events
    const handleStartResponse = (response: { success: boolean; errorMessage?: string }) => {
      setLoadingWithTimeout('start', false);
      if (response.success) {
        message.success(t('bots.control.startSuccess') || 'Bot started successfully');
      } else {
        message.error(
          response.errorMessage || t('bots.control.startError') || 'Failed to start bot'
        );
      }
    };

    const handleStopResponse = (response: { success: boolean; errorMessage?: string }) => {
      setLoadingWithTimeout('stop', false);
      if (response.success) {
        message.success(t('bots.control.stopSuccess') || 'Bot stopped successfully');
      } else {
        message.error(response.errorMessage || t('bots.control.stopError') || 'Failed to stop bot');
      }
    };

    const handleConnectResponse = (response: { success: boolean; errorMessage?: string }) => {
      setLoadingWithTimeout('connect', false);
      if (response.success) {
        message.success(t('bots.control.connectSuccess') || 'Bot connected successfully');
        // Automatically start remote streaming after successful connection
        // Add a small delay to ensure browser is fully ready
        if (socket && socket.connected) {
          setTimeout(() => {
            if (socket && socket.connected) {
              socket.emit('remote-start', {});
              setIsStreaming(true);
              isStreamingRef.current = true;
            }
          }, 1000); // Wait 1 second for browser to be fully ready
        }
      } else {
        message.error(
          response.errorMessage || t('bots.control.connectError') || 'Failed to connect bot'
        );
      }
    };

    const handleDisconnectResponse = (response: { success: boolean; errorMessage?: string }) => {
      setLoadingWithTimeout('disconnect', false);
      if (response.success) {
        message.success(t('bots.control.disconnectSuccess') || 'Bot disconnected successfully');
        // Stop remote streaming when disconnected
        if (socket && socket.connected && isStreamingRef.current) {
          socket.emit('remote-stop', {});
          setIsStreaming(false);
          isStreamingRef.current = false;
          setRemoteFrame(null);
        }
      } else {
        message.error(
          response.errorMessage || t('bots.control.disconnectError') || 'Failed to disconnect bot'
        );
      }
    };

    const handlePauseResponse = (response: { success: boolean; errorMessage?: string }) => {
      setLoadingWithTimeout('pause', false);
      if (response.success) {
        message.success(t('bots.control.pauseSuccess') || 'Bot paused successfully');
      } else {
        message.error(
          response.errorMessage || t('bots.control.pauseError') || 'Failed to pause bot'
        );
      }
    };

    const handleResumeResponse = (response: { success: boolean; errorMessage?: string }) => {
      setLoadingWithTimeout('resume', false);
      if (response.success) {
        message.success(t('bots.control.resumeSuccess') || 'Bot resumed successfully');
      } else {
        message.error(
          response.errorMessage || t('bots.control.resumeError') || 'Failed to resume bot'
        );
      }
    };

    // Throttle frame updates to prevent lag (update max once per second)
    const frameThrottleMs = 1000; // Update UI max once per second

    const handleRemoteFrame = (payload: RemoteFramePayload) => {
      frameCountRef.current += 1;
      const now = Date.now();
      // Only update state if enough time has passed since last update
      if (now - lastFrameUpdateRef.current >= frameThrottleMs) {
        lastFrameUpdateRef.current = now;
        setRemoteFrame(payload);
      }
    };

    // Bot status event listeners
    const handleBotStatus = (payload: BotStatusPayload) => {
      setBotStatus(payload);
      setIsPaused(payload.isPaused);
    };

    const handleBrowserStatus = (payload: BrowserStatusPayload) => {
      setBrowserStatus(payload);
    };

    const handleWorkflowCompleted = (payload: WorkflowCompletedPayload) => {
      setWorkflowStatus((prev) => ({
        ...prev,
        lastCompleted: payload,
        lastError: null,
      }));
      if (payload.success) {
        message.success(
          t('bots.workflow.completed', {
            steps: `${payload.completedSteps}/${payload.totalSteps}`,
          }) || `Workflow completed: ${payload.completedSteps}/${payload.totalSteps} steps`
        );
      } else {
        message.warning(
          t('bots.workflow.completedWithErrors', {
            steps: `${payload.completedSteps}/${payload.totalSteps}`,
            errors: payload.errors.length,
          }) ||
            `Workflow completed with ${payload.errors.length} error(s): ${payload.completedSteps}/${payload.totalSteps} steps`
        );
      }
    };

    const handleWorkflowError = (payload: WorkflowErrorPayload) => {
      setWorkflowStatus((prev) => ({
        ...prev,
        lastError: payload,
      }));
      message.error(
        t('bots.workflow.error', { error: payload.error }) || `Workflow error: ${payload.error}`
      );
    };

    const handleBotPaused = (payload: BotPausedPayload) => {
      setIsPaused(true);
      message.info(
        t('bots.control.paused', { message: payload.message }) || `Bot paused: ${payload.message}`
      );
    };

    const handleBotResumed = (payload: BotResumedPayload) => {
      setIsPaused(false);
      message.success(
        t('bots.control.resumed', { message: payload.message }) || `Bot resumed: ${payload.message}`
      );
    };

    socket.on('bot:start:response', handleStartResponse);
    socket.on('bot:stop:response', handleStopResponse);
    socket.on('bot:connect:response', handleConnectResponse);
    socket.on('bot:disconnect:response', handleDisconnectResponse);
    socket.on('bot:pause:response', handlePauseResponse);
    socket.on('bot:resume:response', handleResumeResponse);
    socket.on('bot:status', handleBotStatus);
    socket.on('browser:status', handleBrowserStatus);
    socket.on('workflow:completed', handleWorkflowCompleted);
    socket.on('workflow:error', handleWorkflowError);
    socket.on('bot:paused', handleBotPaused);
    socket.on('bot:resumed', handleBotResumed);

    socket.on('remote-frame', handleRemoteFrame);

    // Request current status when component mounts and socket is connected
    const requestStatus = () => {
      if (socket && socket.connected) {
        socket.emit('bot:status:get', {});
      }
    };

    // Request status immediately if socket is already connected
    if (socket.connected) {
      requestStatus();
    }

    // Request status when socket connects or reconnects
    const handleConnect = () => {
      requestStatus();
    };

    socket.on('connect', handleConnect);

    // Cleanup listeners on unmount
    return () => {
      socket.off('bot:start:response', handleStartResponse);
      socket.off('bot:stop:response', handleStopResponse);
      socket.off('bot:connect:response', handleConnectResponse);
      socket.off('bot:disconnect:response', handleDisconnectResponse);
      socket.off('bot:pause:response', handlePauseResponse);
      socket.off('bot:resume:response', handleResumeResponse);
      socket.off('bot:status', handleBotStatus);
      socket.off('browser:status', handleBrowserStatus);
      socket.off('workflow:completed', handleWorkflowCompleted);
      socket.off('workflow:error', handleWorkflowError);
      socket.off('bot:paused', handleBotPaused);
      socket.off('bot:resumed', handleBotResumed);
      socket.off('remote-frame', handleRemoteFrame);
      socket.off('connect', handleConnect);
      // Stop remote streaming on unmount
      if (socket && socket.connected && isStreamingRef.current) {
        socket.emit('remote-stop', {});
        setIsStreaming(false);
        isStreamingRef.current = false;
      }
      // Clear all timeouts
      clearActionTimeout('start');
      clearActionTimeout('stop');
      clearActionTimeout('connect');
      clearActionTimeout('disconnect');
      clearActionTimeout('pause');
      clearActionTimeout('resume');
    };
  }, [socket, t]);

  const handleStart = () => {
    if (!checkConnection()) return;
    setLoadingWithTimeout('start', true);
    socket!.emit('bot:start', {});
  };

  const handleStop = () => {
    if (!checkConnection()) return;
    setLoadingWithTimeout('stop', true);
    socket!.emit('bot:stop', {});
  };

  const handleConnect = () => {
    if (!checkConnection()) return;
    setLoadingWithTimeout('connect', true);
    socket!.emit('bot:connect', {});
  };

  const handleDisconnect = () => {
    if (!checkConnection()) return;
    setLoadingWithTimeout('disconnect', true);
    socket!.emit('bot:disconnect', {});
  };

  const handlePause = () => {
    if (!checkConnection()) return;
    setLoadingWithTimeout('pause', true);
    socket!.emit('bot:pause', {});
  };

  const handleResume = () => {
    if (!checkConnection()) return;
    setLoadingWithTimeout('resume', true);
    socket!.emit('bot:resume', {});
  };

  // Get bot status display
  const getBotStatusTag = () => {
    if (!botStatus) {
      return <Tag color="default">{t('bots.status.unknown') || 'Unknown'}</Tag>;
    }

    if (botStatus.isPaused) {
      return <Tag color="orange">{t('bots.status.paused') || 'Paused'}</Tag>;
    }

    if (botStatus.isRunning) {
      return <Tag color="green">{t('bots.status.running') || 'Running'}</Tag>;
    }

    return <Tag color="red">{t('bots.status.stopped') || 'Stopped'}</Tag>;
  };

  // Get browser status display
  const getBrowserStatusTag = () => {
    const status = browserStatus || botStatus;
    if (!status) {
      return <Tag color="default">{t('bots.browser.unknown') || 'Unknown'}</Tag>;
    }

    if (status.hasBrowser && status.hasPage) {
      return <Tag color="green">{t('bots.browser.connected') || 'Connected'}</Tag>;
    }

    return <Tag color="red">{t('bots.browser.disconnected') || 'Disconnected'}</Tag>;
  };

  // Get workflow status display
  const getWorkflowStatusTag = () => {
    console.log('workflowStatus', workflowStatus);
    if (workflowStatus.lastError) {
      return (
        <Tag color="red">
          {t('bots.workflow.error', { error: workflowStatus.lastError.error }) || 'Error'}
        </Tag>
      );
    }

    if (workflowStatus.lastCompleted) {
      const { lastCompleted } = workflowStatus;
      if (lastCompleted.success) {
        return (
          <Tag color="green">
            {t('bots.workflow.completed', {
              steps: `${lastCompleted.completedSteps}/${lastCompleted.totalSteps}`,
            }) || 'Completed'}
          </Tag>
        );
      } else {
        return (
          <Tag color="orange">
            {t('bots.workflow.completedWithErrors', {
              steps: `${lastCompleted.completedSteps}/${lastCompleted.totalSteps}`,
              errors: lastCompleted.errors?.length || 0,
            }) || 'Completed with errors'}
          </Tag>
        );
      }
    }

    return null;
  };

  // Get current URL display
  const getCurrentUrl = () => {
    const url = browserStatus?.currentUrl || botStatus?.currentUrl;
    if (!url) {
      return <Text type="secondary">{t('bots.url.none') || 'No URL'}</Text>;
    }
    return (
      <Text ellipsis style={{ maxWidth: 400 }}>
        <a href={url} target="_blank" rel="noopener noreferrer">
          {url}
        </a>
      </Text>
    );
  };

  const isWorkflowRunning = useCallback(() => {
    return botStatus?.isRunning || botStatus?.isPaused;
  }, [botStatus?.isRunning, botStatus?.isPaused]);

  const isBotRunning = useCallback(() => {
    return botStatus?.isRunning || botStatus?.isPaused;
  }, [botStatus?.isRunning, botStatus?.isPaused]);

  const isBotPaused = useCallback(() => {
    console.log('isBotPaused', botStatus);
    console.log('botStatus', botStatus);
    return botStatus?.isPaused;
  }, [botStatus?.isPaused]);

  const isBotStreamConnected = useCallback(() => {
    return isStreaming;
  }, [isStreaming]);

  return (
    <Card className="shadow-lg mb-3">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {/* Control Buttons */}
        <Space size="middle">
          <Button
            type="primary"
            icon={<PlayCircleOutlined />}
            onClick={handleStart}
            disabled={!isConnected || isPaused}
            loading={loading.start}
          >
            {t('bots.control.start') || 'Start'}
          </Button>
          {isBotRunning() && (
            <Button
              type="default"
              danger
              icon={<StopOutlined />}
              onClick={handleStop}
              disabled={!isConnected}
              loading={loading.stop}
            >
              {t('bots.control.stop') || 'Stop'}
            </Button>
          )}

          {isWorkflowRunning() && !isPaused && (
            <Button
              type="default"
              icon={<PauseCircleOutlined />}
              onClick={handlePause}
              disabled={!isConnected || isPaused}
              loading={loading.pause}
            >
              {t('bots.control.pause') || 'Pause'}
            </Button>
          )}
          {((isWorkflowRunning() && !isConnected) || isPaused) && (
            <Button
              type="default"
              icon={<PlayCircleOutlined />}
              onClick={handleResume}
              disabled={!isConnected || !isPaused}
              loading={loading.resume}
            >
              {t('bots.control.resume') || 'Resume'}
            </Button>
          )}
          {isWorkflowRunning() && !isBotStreamConnected() && (
            <Button
              type="default"
              icon={<LinkOutlined />}
              onClick={handleConnect}
              disabled={!isConnected}
              loading={loading.connect}
            >
              {t('bots.control.connect') || 'Connect'}
            </Button>
          )}
          {isBotStreamConnected() && (
            <Button
              type="default"
              icon={<DisconnectOutlined />}
              onClick={handleDisconnect}
              disabled={!isConnected}
              loading={loading.disconnect}
            >
              {t('bots.control.disconnect') || 'Disconnect'}
            </Button>
          )}
        </Space>

        {/* Status Indicators */}
        <Row gutter={[16, 8]}>
          <Col span={24}>
            <Space size="large" wrap>
              <div>
                <Text strong>{t('bots.status.label') || 'Bot Status'}: </Text>
                {getBotStatusTag()}
              </div>
              <div>
                <Text strong>{t('bots.browser.label') || 'Browser Status'}: </Text>
                {getBrowserStatusTag()}
              </div>
              {getWorkflowStatusTag() && (
                <div>
                  <Text strong>{t('bots.workflow.label') || 'Workflow'}: </Text>
                  {getWorkflowStatusTag()}
                </div>
              )}
            </Space>
          </Col>
          {isStreaming && (
            <Col span={24}>
              <div>
                <Text strong>{t('bots.remote.streaming') || 'Remote View'}: </Text>
                {remoteFrame ? (
                  <div
                    style={{
                      marginTop: 8,
                      border: '1px solid #d9d9d9',
                      borderRadius: 4,
                      padding: 8,
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <div
                      ref={previewContainerRef}
                      style={{
                        position: 'relative',
                        display: 'inline-block',
                        width: '100%',
                        cursor: 'pointer',
                        outline: 'none',
                      }}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (!socket || !socket.connected || !remoteFrame) return;
                        // Handle special keys
                        if (e.key === 'Enter' || e.key === 'Tab' || e.key.startsWith('Arrow')) {
                          e.preventDefault();
                          socket.emit('remote-key', {
                            type: 'down',
                            key: e.key,
                          });
                        } else if (e.key.length === 1) {
                          // Regular character
                          e.preventDefault();
                          socket.emit('remote-key', {
                            type: 'type',
                            text: e.key,
                          });
                        }
                      }}
                      onKeyUp={(e) => {
                        if (!socket || !socket.connected || !remoteFrame) return;
                        if (e.key === 'Enter' || e.key === 'Tab' || e.key.startsWith('Arrow')) {
                          e.preventDefault();
                          socket.emit('remote-key', {
                            type: 'up',
                            key: e.key,
                          });
                        }
                      }}
                      onMouseDown={(e) => {
                        if (!socket || !socket.connected || !remoteFrame) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const img = e.currentTarget.querySelector('img');
                        if (!img) return;

                        const imgRect = img.getBoundingClientRect();
                        const x = (e.clientX - imgRect.left) / imgRect.width;
                        const y = (e.clientY - imgRect.top) / imgRect.height;

                        // Normalize coordinates (0-1)
                        const normalizedX = Math.max(0, Math.min(1, x));
                        const normalizedY = Math.max(0, Math.min(1, y));

                        socket.emit('remote-mouse', {
                          type: 'down',
                          x: normalizedX,
                          y: normalizedY,
                          button: e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left',
                        });
                      }}
                      onMouseUp={(e) => {
                        if (!socket || !socket.connected || !remoteFrame) return;
                        const rect = e.currentTarget.getBoundingClientRect();
                        const img = e.currentTarget.querySelector('img');
                        if (!img) return;

                        const imgRect = img.getBoundingClientRect();
                        const x = (e.clientX - imgRect.left) / imgRect.width;
                        const y = (e.clientY - imgRect.top) / imgRect.height;

                        const normalizedX = Math.max(0, Math.min(1, x));
                        const normalizedY = Math.max(0, Math.min(1, y));

                        socket.emit('remote-mouse', {
                          type: 'up',
                          x: normalizedX,
                          y: normalizedY,
                          button: e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left',
                        });
                      }}
                      onClick={(e) => {
                        if (!socket || !socket.connected || !remoteFrame) return;
                        e.preventDefault();
                        const img = e.currentTarget.querySelector('img');
                        if (!img) return;

                        const imgRect = img.getBoundingClientRect();
                        const x = (e.clientX - imgRect.left) / imgRect.width;
                        const y = (e.clientY - imgRect.top) / imgRect.height;

                        const normalizedX = Math.max(0, Math.min(1, x));
                        const normalizedY = Math.max(0, Math.min(1, y));

                        socket.emit('remote-mouse', {
                          type: 'click',
                          x: normalizedX,
                          y: normalizedY,
                          button: e.button === 2 ? 'right' : e.button === 1 ? 'middle' : 'left',
                        });
                      }}
                      onDoubleClick={(e) => {
                        if (!socket || !socket.connected || !remoteFrame) return;
                        e.preventDefault();
                        const img = e.currentTarget.querySelector('img');
                        if (!img) return;

                        const imgRect = img.getBoundingClientRect();
                        const x = (e.clientX - imgRect.left) / imgRect.width;
                        const y = (e.clientY - imgRect.top) / imgRect.height;

                        const normalizedX = Math.max(0, Math.min(1, x));
                        const normalizedY = Math.max(0, Math.min(1, y));

                        socket.emit('remote-mouse', {
                          type: 'dblclick',
                          x: normalizedX,
                          y: normalizedY,
                          button: 'left',
                        });
                      }}
                      onContextMenu={(e) => {
                        e.preventDefault(); // Prevent browser context menu
                      }}
                    >
                      <img
                        src={remoteFrame.image}
                        alt="Remote browser view"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          display: 'block',
                          border: '1px solid #e8e8e8',
                          borderRadius: 2,
                          userSelect: 'none',
                          pointerEvents: 'none', // Let parent handle events
                        }}
                        draggable={false}
                        onLoad={() => {
                          // Image loaded successfully
                        }}
                        onError={(e) => {
                          console.error('[React] Error loading screenshot image', e);
                          console.error('[React] Image src length:', remoteFrame.image?.length);
                          console.error(
                            '[React] Image src preview:',
                            remoteFrame.image?.substring(0, 100)
                          );
                        }}
                      />
                    </div>
                    <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 8 }}>
                      {remoteFrame.width}x{remoteFrame.height} -{' '}
                      {new Date(remoteFrame.timestamp).toLocaleTimeString()} - Frame #
                      {frameCountRef.current}{' '}
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        (Click to interact • Right-click for context menu • Scroll to zoom • Click
                        image then type for keyboard input)
                      </Text>
                    </Text>
                  </div>
                ) : (
                  <div
                    style={{
                      marginTop: 8,
                      padding: 16,
                      textAlign: 'center',
                      border: '1px dashed #d9d9d9',
                      borderRadius: 4,
                      backgroundColor: '#fafafa',
                    }}
                  >
                    <Text type="secondary">
                      Waiting for frames... (Streaming: {isStreaming ? 'Active' : 'Inactive'})
                    </Text>
                  </div>
                )}
              </div>
            </Col>
          )}
        </Row>
      </Space>
    </Card>
  );
};

export default BotControlComponent;
